import { defineStore } from 'pinia';
import {
    typeToColorCategorySelected,
    typeToColorSelected,
} from '@/Constants/common';
import { useUIOnlyStore } from '@/Store/storeUI';
import { replaceItemsOnColorChange } from '@/lib/replaceItemsOnColorChange';
import { configurationStoreDefaultState } from '@/Constants/configurationStoreDefaultState';
import { itemColorOptions } from '@/Constants/demoData';
import { $ } from '@/Engine/state';
import { debounce } from 'lodash';
import { getAllItemIDs } from '@/lib/getAllItemIDs';

let historyPointer = 0;
let isRestoring = false;
let restoreTimeout;

function _saveToHistory(state) {
    let historyString = localStorage.getItem('projectHistory');
    let history;
    if (!historyString || !historyString.length) {
        history = [ state ];
    } else {
        history = JSON.parse(historyString);
        if (history.length > 30) {
            history.splice(0, 1);
        }
        if (historyPointer < history.length - 1) {
            history.splice(historyPointer+1, history.length);
        }
        historyPointer = history.length;
        // console.log('save: ', historyPointer);
        history.push(JSON.parse(JSON.stringify(state)));
    }
    localStorage.setItem('projectHistory', JSON.stringify(history));
}

const saveToHistory = debounce((state)=>{
    if (isRestoring) return;
    _saveToHistory(state);
}, 300);
export function restoreFromHistory(direction) {
    const history = JSON.parse(localStorage.getItem('projectHistory'));
    if (
        historyPointer + direction >  history.length ||
        historyPointer + direction < 0
    ) return;
    isRestoring = true;


    historyPointer+=direction;
    // console.log('pointer: ',historyPointer);
    const state = history[historyPointer];

    useConfigurationStore().$patch(state);
    if (restoreTimeout)
        clearTimeout(restoreTimeout);

    restoreTimeout = setTimeout(()=>{
        isRestoring = false;
    }, 1000);


}

export const useConfigurationStore = defineStore('configurationStore', {
    state: () => JSON.parse(JSON.stringify(configurationStoreDefaultState)),
    getters: {
        getDoors: (state) => {
            return state.wardrobe.doors;
        },
        getBoxes: (state) => {
            return state.wardrobe.boxes;
        },
        getWindows: (state) => {
            return state.wardrobe.windows;
        },
        getProjectName:(state)=>{
          return state.projectName;
        },
        getSelectedDoor: (state) => {
            const selectedObject = useUIOnlyStore().getSelectedSceneObject.uuid;
            return state.wardrobe.doors.filter((item) => item.uuid === selectedObject)[0];
        },
        getSelectedWindow: (state) => {
            const selectedObject = useUIOnlyStore().getSelectedSceneObject.uuid;
            return state.wardrobe.windows.filter((item) => item.uuid === selectedObject)[0];
        },
        getModules: (state) => {
            return state.wardrobe.modules;
        },
        getShelves: (state) => {
            return state.wardrobe.shelves;
        },
        getRails: (state) => {
            return state.wardrobe.rails;
        },
        getByUUID(){
            return (uuid) =>
            this.wardrobe.modules.find((module)=>module.uuid === uuid);
        },
        getShelfByUUID(){
            return (uuid) =>
                this.wardrobe.shelves.find((module)=>module.uuid === uuid);
        },
        getRailByUUID(){
            return (uuid) =>
                this.wardrobe.rails.find((module)=>module.uuid === uuid);
        },
        getRoomParamByName: (state) => {
            return (paramName) => {
                return state.room[paramName];
            };
        },
        getWardrobeBaseColor: (state) => {
            return state.wardrobeBaseColor;
        },
        getWardrobeBaseColorName: (state) => {
            let colorHex = state.wardrobeBaseColor;
            return itemColorOptions.filter((item) => item.img === colorHex)[0].name;
        },
        getAllItemIDs: (state)=>getAllItemIDs(state.wardrobe),
    },
    actions: {
        clear() {
            this.room = JSON.parse(JSON.stringify(configurationStoreDefaultState.room));
            this.wardrobeBaseColor = JSON.parse(JSON.stringify(configurationStoreDefaultState.wardrobeBaseColor));
            this.wardrobe =  {
                    modules: [],
                    shelves: [],
                    rails: [],
                    windows: [],
                    doors: [],
                    boxes: [],
            };
            this.projectName = JSON.parse(JSON.stringify(configurationStoreDefaultState.projectName));
            saveToHistory(this.$state);
        },
        // removeEmptyModules() {
        //   this.wardrobe.modules.forEach((module)=>{
        //       if (!module.children.length) this.removeByUUID(module.uuid);
        //   });
        // },
        updateModules(data) {
            this.wardrobe.modules = JSON.parse(JSON.stringify(data));
            saveToHistory(this.$state);
        },
        updateShelves(data) {
            this.wardrobe.shelves = JSON.parse(JSON.stringify(data));
            saveToHistory(this.$state);
        },
        updateRails(data) {
            this.wardrobe.rails = JSON.parse(JSON.stringify(data));
            saveToHistory(this.$state);
        },
        updateModule(data) {
            const moduleIndex = this.wardrobe.modules.findIndex((module)=>module.uuid === data.uuid);
            this.wardrobe.modules[moduleIndex] = JSON.parse(JSON.stringify(data));
            saveToHistory(this.$state);
        },
        // updateShelves(data) {
        //     getShelfByUUID
        //     this.wardrobe.shelves = JSON.parse(JSON.stringify(data));
        // },
        setRoom(value) {
            this.room = { ...this.room, ...value };
            saveToHistory(this.$state);
        },
        setWardrobeBaseColor(value) {
            this.wardrobeBaseColor = value;
            saveToHistory(this.$state);
        },
        setPositionByUUID(uuid, position, side) {
            const module = this.getByUUID(uuid);
            if (module) {
                module.position = position;
                if (side) module.side = side;
                saveToHistory(this.$state);
            }
        },
        setWindowPositionByUUID(uuid, position, side) {
            const window = this.wardrobe.windows.find((module)=>module.uuid === uuid);
            if (window) {
                window.position = position;
                if (side) window.side = side;
                saveToHistory(this.$state);
            }
        },
        setDoorPositionByUUID(uuid, position, side) {
            const door = this.wardrobe.doors.find((module)=>module.uuid === uuid);
            if (door) {
                door.position = position;
                if (side) door.side = side;
                saveToHistory(this.$state);
            }
        },
        setBoxPositionByUUID(uuid, position) {
            const box = this.wardrobe.boxes.find((item)=>item.uuid === uuid);
            if (box) {
                box.position = position;
                saveToHistory(this.$state);
            }
        },
        // setChildByUUID(uuid, child) {
        //     const module = this.getByUUID(uuid);
        //     if (module) {
        //         module.children.push(JSON.parse(JSON.stringify(child)));
        //         module.needUpdate = true;
        //     }
        //     // this.removeEmptyModules();
        // },
        // updateChildByUUID(moduleUUID, child) {
        //     const module = this.getByUUID(moduleUUID);
        //     const childIndex = module.children.findIndex((item)=>item.uuid === child.uuid);
        //     const newChildren = JSON.parse(JSON.stringify(module.children));
        //
        //     newChildren.splice(childIndex, 1);
        //     newChildren.push(JSON.parse(JSON.stringify(child)));
        //     module.children = newChildren;
        // },
        updateShelf(data) {
            const shelfIndex = this.wardrobe.shelves.findIndex((module)=>module.uuid === data.uuid);
            this.wardrobe.shelves[shelfIndex] = JSON.parse(JSON.stringify(data));
            saveToHistory(this.$state);
        },
        removeBoxByUUID(uuid) {
            const id = this.wardrobe.boxes.findIndex((box)=>box.uuid === uuid);

            if (id === -1) return false;
            this.wardrobe.boxes.splice(id, 1);
            $.selectionBoxForBox.unselect();
        },
        removeByUUID(label,uuid) {
            // console.log('',label,this.wardrobe);
            if(uuid==='temp') return;
            let railUUID='';
            switch (label) {
                case 'Группа секций':
                    //Получить массив всех модулей на рельсе этой секции
                    railUUID = this.wardrobe.modules.find((module) => module.uuid === uuid)?.railGroupUUID;
                    let sections = this.wardrobe.modules.filter((module) => module.railGroupUUID === railUUID);
                    sections.map((module) => this.removeModuleByUUID(module.uuid));
                    break;
                case 'Секция':
                    railUUID = this.wardrobe.modules.find((module) => module.uuid === uuid).railGroupUUID;
                    this.removeModuleByUUID(uuid);
                    break;
                default:
                    let shelfRackUUID = this.wardrobe.shelves.find((shelf) => shelf.uuid === uuid).rackModuleUUID;
                    this.removeShelfByUUID(uuid);
                    //Удаление пустых реек
                    if (!this.wardrobe.shelves.some((shelf) => shelf.rackModuleUUID === shelfRackUUID)) {
                        railUUID = this.wardrobe.modules.find((module) => module.uuid === shelfRackUUID).railGroupUUID;
                        this.removeModuleByUUID(shelfRackUUID);
                    }
                    break;
            }
            //Удаление пустых рельс
            if (!this.wardrobe.modules.some((module) => module.railGroupUUID === railUUID)) this.removeRailByUUID(railUUID);
            useUIOnlyStore().setDeleteFlag(true);
        },
        removeShelfByUUID(uuid){
            const index = this.wardrobe.shelves.findIndex((shelf)=>shelf.uuid === uuid);
            if (index !== -1) {
                const newArray = JSON.parse(JSON.stringify(this.wardrobe.shelves));
                newArray.splice(index, 1);
                this.wardrobe.shelves = newArray;
                saveToHistory(this.$state);
            }
            $.selectionBox.unselect();
        },
        removeModuleByUUID(uuid){
            const index = this.wardrobe.modules.findIndex((module)=>module.uuid === uuid);
            if (index !== -1) {
                const newArray = JSON.parse(JSON.stringify(this.wardrobe.modules));
                newArray.splice(index, 1);
                this.wardrobe.modules = newArray;
                saveToHistory(this.$state);
            }
            //Удаление полок, установленных на удаленную рейку
            this.wardrobe.shelves.forEach((shelf)=>{
                if(shelf.rackModuleUUID===uuid) this.removeShelfByUUID(shelf.uuid);
            });
            $.selectionBox.unselect();
        },
        removeRailByUUID(uuid){
            const index = this.wardrobe.rails.findIndex((module)=>module.uuid === uuid);
            if (index !== -1) {
                const newArray = JSON.parse(JSON.stringify(this.wardrobe.rails));
                newArray.splice(index, 1);
                this.wardrobe.rails = newArray;
                saveToHistory(this.$state);
            }
        },
        removeDoorByUUID(uuid) {
            const index = this.wardrobe.doors.findIndex((module)=>module.uuid === uuid);
            if (index === -1) return;
            const newArray = JSON.parse(JSON.stringify(this.wardrobe.doors));
            newArray.splice(index, 1);
            $.selectionBox.unselect();
            this.wardrobe.doors = newArray;
            saveToHistory(this.$state);
        },
        removeWindowByUUID(uuid) {
            const index = this.wardrobe.windows.findIndex((module)=>module.uuid === uuid);
            if (index === -1) return;
            const newArray = JSON.parse(JSON.stringify(this.wardrobe.windows));
            newArray.splice(index, 1);
            $.selectionBox.unselect();
            this.wardrobe.windows = newArray;
            saveToHistory(this.$state);
            // selectionBox.unselect();
        },
        addModule(module) {
            if (module.uuid === 'temp')
                module.uuid = crypto.randomUUID();

            if (!this.wardrobe.modules.length) {
                this.wardrobeBaseColor = '#'+(module.color.replace('#','') || 'ffffff');
            }

            if (this.getByUUID(module.uuid)) {
                this.updateModule(module);
                return;
            }

            this.wardrobe.modules.push(JSON.parse(JSON.stringify(module)));
            saveToHistory(this.$state);
        },
        addShelf(shelf) {
            if (shelf.uuid === 'temp') {
                shelf.uuid = crypto.randomUUID();
            }
            if (!this.wardrobe.shelves.length) {
                this.wardrobeBaseColor = '#'+shelf.color.replace('#','');
            }
            if (this.getShelfByUUID(shelf.uuid)) {
                this.updateShelf(shelf);
                return;
            }
            this.wardrobe.shelves.push(JSON.parse(JSON.stringify(shelf)));
            saveToHistory(this.$state);
        },
        addRailGroup(rail) {
            if (rail.uuid === 'temp')
                rail.uuid = crypto.randomUUID();
            if (this.getRailByUUID(rail.uuid)) {
                this.updateRailGroup(rail);
                return;
            }
            this.wardrobe.rails.push(JSON.parse(JSON.stringify(rail)));
            saveToHistory(this.$state);
        },
        addKit(data) {
            this.wardrobe = {
                ...JSON.parse(JSON.stringify(this.wardrobe)),
                shelves: JSON.parse(JSON.stringify(data.shelves)),
                rails: JSON.parse(JSON.stringify(data.rails)),
                modules: JSON.parse(JSON.stringify(data.modules)),
            };

            saveToHistory(this.$state);
        },
        updateRailGroup(rail) {
            const railGroupIndex = this.wardrobe.rails.findIndex((item)=>item.uuid === rail.uuid);
            if (railGroupIndex === -1) return;
            this.wardrobe.rails[railGroupIndex] = JSON.parse(JSON.stringify(rail));
            saveToHistory(this.$state);
        },
        addWindow(window) {
            window.uuid = crypto.randomUUID();
            this.wardrobe.windows.push(window);
            saveToHistory(this.$state);
        },
        addDoor(door) {
            door.uuid = crypto.randomUUID();
            this.wardrobe.doors.push(door);
            saveToHistory(this.$state);
        },
        addBox(box) {
            box.uuid = crypto.randomUUID();
            this.wardrobe.boxes.push(box);
            saveToHistory(this.$state);
        },
        setRoomParamByName(name, value) {
            this.room[name] = value;
            saveToHistory(this.$state);
        },
        setColorCategory(type, colorId) {
            this.room[typeToColorCategorySelected[type]] = +colorId;
            saveToHistory(this.$state);
        },
        setColor(type, colorId) {
            this.room[typeToColorSelected[type]] = +colorId;
            saveToHistory(this.$state);
        },
        setMainColor(value)  {
            this.wardrobeBaseColor = value;
            replaceItemsOnColorChange();
            // Важный костыль повторно обновляет конфигурацию, чтобы применились все замены штанг и т.п.
            setTimeout(()=>{
                this.addKit($.wardrobe.raw);
            }, 100);
        },
        setWindowSize(uuid, width, height) {
            const index = this.wardrobe.windows.findIndex((module)=>module.uuid === uuid);
            if (index === -1) return;
            const newArray = JSON.parse(JSON.stringify(this.wardrobe.windows));

            newArray[index].width = width;
            newArray[index].height = height;

            this.wardrobe.windows = newArray;
            saveToHistory(this.$state);
        },
        setDoorSize(uuid, width, height) {
            const index = this.wardrobe.doors.findIndex((module)=>module.uuid === uuid);
            if (index === -1) return;
            const newArray = JSON.parse(JSON.stringify(this.wardrobe.doors));

            newArray[index].width = width;
            newArray[index].height = height;

            this.wardrobe.doors = newArray;
            saveToHistory(this.$state);
        },
        setBoxSize(uuid, width, height, depth, floorHeight) {
            const index = this.wardrobe.boxes.findIndex((module)=>module.uuid === uuid);
            if (index === -1) return;
            const newArray = JSON.parse(JSON.stringify(this.wardrobe.boxes));

            newArray[index].width = width;
            newArray[index].height = height;
            newArray[index].depth = depth;
            newArray[index].floorHeight = floorHeight;
            this.wardrobe.boxes = newArray;

            saveToHistory(this.$state);
        },
        setProjectName(name) {
            this.projectName=name;
            saveToHistory(this.$state);
        },
    },
    persist: true,
});
